import { Fragment } from 'react';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';

import { QUESTION_TYPES } from '@api/instant_survey';

import OptionLogicRow from './components/OptionLogicRow';
import QuestionLogicOptions from '../../../../../../../QuestionLogic/components/QuestionLogicOptions';
import {
  answerOperators,
  skipOperators,
} from '../../../../../../../QuestionLogic/utilities/constants';
import { MinusPlusButtons, Pill } from '@utilities';

import styles from './_index.module.scss';

const OptionLogic = ({
  answer,
  firstErrorRef,
  question,
  questionIndex,
  questionList,
  questionLogic,
  setQuestionLogic,
}) => {
  const isGrid = question.type === QUESTION_TYPES['TABLE-RADIO'].value;
  const optionLogic = questionLogic?.[question.id]?.option?.[answer.id];

  const handleAddLogicRow = () => {
    const updatedLogic = cloneDeep(questionLogic);
    updatedLogic[question.id].option[answer.id].logic = [
      ...updatedLogic[question.id].option[answer.id].logic,
      {
        answers: [],
        operator: answerOperators[0],
        question: {},
      },
    ];
    setQuestionLogic(updatedLogic);
  };

  const handleRemoveLogicRow = (index) => {
    const updatedLogic = cloneDeep(questionLogic);

    if (updatedLogic[question.id].option[answer.id].logic.length === 1) {
      // Last logic row being removed - delete the entire answer option
      delete updatedLogic[question.id].option[answer.id];
    } else {
      // Remove specific row and check if result is now empty
      updatedLogic[question.id].option[answer.id].logic.splice(index, 1);

      if (updatedLogic[question.id].option[answer.id].logic.length === 0) {
        delete updatedLogic[question.id].option[answer.id];
      }
    }

    // Clean up empty parent objects for current question
    if (
      updatedLogic[question.id]?.option &&
      Object.keys(updatedLogic[question.id].option).length === 0
    ) {
      delete updatedLogic[question.id].option;
    }

    if (updatedLogic[question.id] && Object.keys(updatedLogic[question.id]).length === 0) {
      delete updatedLogic[question.id];
    }

    // Clean up references to this deleted answer option in subsequent questions
    Object.keys(updatedLogic).forEach((questionKey) => {
      if (updatedLogic[questionKey].option) {
        Object.keys(updatedLogic[questionKey].option).forEach((answerKey) => {
          const optionLogicForAnswer = updatedLogic[questionKey].option[answerKey];

          if (optionLogicForAnswer.logic) {
            // Filter out logic rows that reference the deleted question/answer
            optionLogicForAnswer.logic = optionLogicForAnswer.logic.filter((logicRow) => {
              if (logicRow.question?.id === question.id) {
                //Numeric/quota group logic shouldn't be affected by answer deletion
                if (!Array.isArray(logicRow.answers)) return true;
                // Remove this answer from the answers array
                logicRow.answers = logicRow.answers.filter((ans) => ans.id !== answer.id);
                // Keep the row only if there are still answers
                return logicRow.answers.length > 0;
              }
              return true;
            });

            // If no logic rows remain, delete the answer option
            if (optionLogicForAnswer.logic.length === 0) {
              delete updatedLogic[questionKey].option[answerKey];
            }
          }
        });

        // Clean up empty option object
        if (Object.keys(updatedLogic[questionKey].option).length === 0) {
          delete updatedLogic[questionKey].option;
        }
      }

      // Clean up empty question object
      if (Object.keys(updatedLogic[questionKey]).length === 0) {
        delete updatedLogic[questionKey];
      }
    });

    setQuestionLogic(updatedLogic);
  };

  const updateOperator = (operator) => {
    const updatedLogic = cloneDeep(questionLogic);
    updatedLogic[question.id].option[answer.id].operator = operator;
    setQuestionLogic(updatedLogic);
  };

  if (!optionLogic) {
    return null;
  }

  const optionPillLogic = optionLogic.operator?.value === 'ANY' ? 'OR' : 'AND';

  return (
    <>
      <div className={styles['option-logic']}>
        <span className={styles['option-logic-text']}>
          <span>Display this {isGrid ? 'row' : 'answer option'} if&nbsp;</span>
          <QuestionLogicOptions
            options={skipOperators}
            optionsLabel="Operator"
            optionsType="radio"
            setValue={updateOperator}
            value={questionLogic?.[question.id]?.option?.[answer.id]?.operator}
          />
          <span>&nbsp;apply:</span>
        </span>
        <span className={styles['option-logic-minus-plus-buttons']}>
          <MinusPlusButtons
            disablePlusButton={optionLogic.logic.length >= 3}
            hasPlusButton
            isNeutral
            onClickMinus={handleRemoveLogicRow}
            onClickPlus={handleAddLogicRow}
          />
        </span>
      </div>
      {optionLogic.logic.map((_, index) => {
        return (
          <Fragment key={`${answer.id}-option-${index}`}>
            <div className={styles['option-logic-line']} />
            {index !== 0 && (
              <>
                <Pill className={styles['option-logic-pill']} isDisabled value={optionPillLogic} />
                <div className={styles['option-logic-line']} />
              </>
            )}
            <div className={styles['option-logic']}>
              <span className={styles['option-logic-text']}>
                <OptionLogicRow
                  answerId={answer.id}
                  firstErrorRef={firstErrorRef}
                  index={index}
                  questionId={question.id}
                  questionIndex={questionIndex}
                  questionList={questionList}
                  questionLogic={questionLogic}
                  setQuestionLogic={setQuestionLogic}
                />
              </span>
              <span className={styles['option-logic-minus-plus-buttons']}>
                <MinusPlusButtons
                  hasPlusButton={false}
                  isNeutral
                  onClickMinus={(event) => {
                    event.stopPropagation();
                    handleRemoveLogicRow(index);
                  }}
                />
              </span>
            </div>
          </Fragment>
        );
      })}
    </>
  );
};

OptionLogic.propTypes = {
  answer: PropTypes.object.isRequired,
  firstErrorRef: PropTypes.object,
  question: PropTypes.object.isRequired,
  questionIndex: PropTypes.number.isRequired,
  questionList: PropTypes.array.isRequired,
  questionLogic: PropTypes.object.isRequired,
  setQuestionLogic: PropTypes.func.isRequired,
};

export default OptionLogic;
